import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import Table from '@components/table';

import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { getGiftCardStatusesList } from '@utils/billing';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';

import Input from '@components/common/input';
import { formatDate, getGiftCardStatusVariant } from '@utils/index';
import Dropdown from '@components/common/dropdown';
import Status from '@components/common/status';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';
import emptyIllustration from '@svg/illustrations/empty_gift_cards.svg';
import Skeleton from '@components/common/skeleton';
import {
    EGiftCardStatus,
    TGetGiftCardsFilter,
    TGiftCardListItem,
    TGiftCardListResponse
} from '@xeppt/xeppt-sdk/types';
import { useGiftCardsModals } from '@hooks/modals/useGiftCardsModals';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiGiftCardsService } from '@api';

interface IProps {
    onSearch: (value: string) => void;
    refetchGiftCards: (request?: { isNew: boolean; filter?: TGetGiftCardsFilter }) => void;
    search: string;
    selectedCards: string[];
    onChangeSelectedCards: (val: string[]) => void;
    isLoading?: boolean;
    giftCards: TGiftCardListItem[];
    giftCardsData?: TGiftCardListResponse;
    filter: Omit<TGetGiftCardsFilter, 'cursor' | 'limit'>;
    onFilterChange: (val: TGetGiftCardsFilter) => void;
}

const CardsTable: FC<IProps> = ({
    giftCards,
    giftCardsData,
    refetchGiftCards,
    onSearch,
    filter,
    search,
    onFilterChange,
    selectedCards,
    onChangeSelectedCards,
    isLoading
}) => {
    const navigate = useNavigate();
    const { placeholderLocale } = useLocales();
    const [id] = useQueryParams(['id']);
    const {
        handleOpenViewGiftCardModal,
        handleOpenActivateGiftCardModal,
        handleOpenScanGiftCard,
        handleCancelGiftCards,
        handleRequestCancelGiftCards
    } = useGiftCardsModals({
        refetchGiftCards: () => refetchGiftCards({ isNew: true })
    });
    const { submitLocale, requestErrorLocale, requestSuccessLocale } = useLocales();
    const loader = useRef(null);

    useEffect(() => {
        if (id) {
            handleOpenViewGiftCardModal(id as string);
        }
    }, [id]);

    const { handleRequest: handleSentNotification } = useApiMutation({
        method: (id: string) => apiGiftCardsService.sendGiftCard(id),
        onSuccess: () => {
            requestSuccessLocale('gift_card_sent_successfully');
        },
        onError: requestErrorLocale
    });

    const columns = [
        {
            label: 'Gift card number',
            key: 'id',
            isSort: true,
            render: (_: string, data: TGiftCardListItem) => data.gift_card_serial_number
        },
        {
            label: 'Original amount',
            key: 'amount',
            render: (val: number) => `$${val.toFixed(2)}`,
            isSort: true
        },
        {
            label: 'Remaining balance',
            key: 'available_amount',
            render: (val: number) => `$${val.toFixed(2)}`,
            isSort: true
        },
        {
            label: 'Issued',
            key: 'created_at',
            render: (value: Date) => formatDate(value, 'MMM DD, YYYY HH:mm'),
            isSort: true
        },
        {
            label: 'Recipient',
            key: 'recipient',
            render: (_: undefined, data: TGiftCardListItem) =>
                data?.email
                    ? data?.email
                    : data?.phone_code && data?.phone_number
                      ? `${data.phone_code.includes('+') ? data.phone_code : '+' + data.phone_code} ${data.phone_number}`
                      : '-'
        },
        {
            label: 'Status',
            key: 'status',
            render: (value: EGiftCardStatus) => (
                <Status variant={getGiftCardStatusVariant(value)}>
                    {getGiftCardStatusesList.find((item) => item.value === value)?.label}
                </Status>
            ),
            filterOptions: [
                { label: 'Issued', value: EGiftCardStatus.ISSUED },
                { label: 'Pending', value: EGiftCardStatus.PENDING },
                { label: 'Active', value: EGiftCardStatus.ACTIVE },
                { label: 'Partially redeemed', value: EGiftCardStatus.PARTIALLY_REDEEMED },
                { label: 'Redeemed', value: EGiftCardStatus.REDEEMED },
                { label: 'Cancel requested', value: EGiftCardStatus.CANCEL_REQUESTED },
                { label: 'Cancelled', value: EGiftCardStatus.CANCELLED }
            ]
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_: undefined, data: TGiftCardListItem) => (
                <div className={styles.table_actions}>
                    <Dropdown
                        items={[
                            {
                                label: submitLocale('view_details'),
                                icon: 'open_eye',
                                onClick: () => handleOpenViewGiftCardModal(data.id)
                            },
                            data.status === EGiftCardStatus.ISSUED && {
                                label: submitLocale('activate'),
                                icon: 'power',
                                onClick: () => handleOpenActivateGiftCardModal([data]),
                                isDisabled: !data.email && (!data.phone_number || !data.phone_code),
                                disabledString: 'No recipient email or phone number has been added'
                            },
                            data.status === EGiftCardStatus.PENDING && {
                                label: submitLocale('resend'),
                                icon: 'send',
                                onClick: () => handleSentNotification(data.id)
                            },
                            // {
                            //     label: submitLocale('download_pdf'),
                            //     icon: 'download',
                            //     onClick: () => {}
                            // },
                            data.status !== EGiftCardStatus.REDEEMED &&
                                data.status !== EGiftCardStatus.CANCELLED &&
                                data.status !== EGiftCardStatus.CANCEL_REQUESTED && {
                                    label: submitLocale('cancel'),
                                    isRed: true,
                                    icon: 'close_outlined',
                                    onClick: () => {
                                        if (data.status === EGiftCardStatus.ISSUED) {
                                            handleCancelGiftCards([data]);
                                        } else {
                                            handleRequestCancelGiftCards([data]);
                                        }
                                    }
                                }
                        ]}
                    />
                </div>
            )
        }
    ];

    return (
        <motion.div {...pageAnimation} className={styles.content}>
            <div className={styles.content}>
                <div className={styles.search_wrapper}>
                    <Input
                        value={search}
                        onChange={(val) => onSearch(val)}
                        full
                        className={styles.search}
                        leftIcon="search"
                        rightIcon={search.length > 0 ? 'filed_close' : undefined}
                        onClickRightIcon={() => onSearch('')}
                        placeholder={placeholderLocale('search_gift_card')}
                    />
                    <div className={styles.actions}>
                        <Button
                            variant="outlined-primary"
                            size="medium"
                            onClick={handleOpenScanGiftCard}>
                            {submitLocale('redeem_gift_card')}
                        </Button>
                        <Button
                            variant="primary"
                            size="medium"
                            onClick={() => navigate(routes.gift_cards_create)}>
                            {submitLocale('create_new_gift_card')}
                        </Button>
                    </div>
                </div>
            </div>
            {isLoading && giftCards.length === 0 ? (
                <>
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                </>
            ) : giftCards.length === 0 ? (
                <div className={styles.empty}>
                    <img src={emptyIllustration} alt="empty payees" />
                    <p className={styles.title}>No gift cards have been created yet</p>
                    <p className={styles.description}>
                        Once you create a gift card, it will appear here
                    </p>
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={() => navigate(routes.gift_cards_create)}>
                        {submitLocale('create_new_gift_card')}
                    </Button>
                </div>
            ) : (
                <>
                    <Table
                        //@ts-ignore
                        columns={columns}
                        rows={giftCards}
                        className={styles.table}
                        isActions
                        //@ts-ignore
                        filter={filter}
                        selectKey="id"
                        selected={selectedCards}
                        onSelectedChange={onChangeSelectedCards}
                        onFilterChange={(val) => {
                            onFilterChange({
                                ...filter,
                                ...val,
                                page: 1
                            });
                        }}
                        onSortChange={(sortKey, sortOrder) => {
                            onFilterChange({
                                ...filter,
                                order_field: sortKey as any,
                                order: sortOrder,
                                page: 1
                            });
                        }}
                        totalRows={giftCardsData?.total || 0}
                        sort={{ key: filter.order_field || '', value: filter.order || 'DESC' }}
                        onLoadData={() => {
                            onFilterChange({ ...filter, page: filter.page + 1 });
                        }}
                    />
                    <div ref={loader} />
                </>
            )}
        </motion.div>
    );
};

export default CardsTable;
